(function () {
  'use strict';

  const EDGE_FUNCTION_URL = 'https://buekcbegtbjywedagoai.supabase.co/functions/v1/chat-agent';
  const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImJ1ZWtjYmVndGJqeXdlZGFnb2FpIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Nzg3MTEyMzgsImV4cCI6MjA5NDI4NzIzOH0.EI5IN9efHA2slJk-941QulvWjfn7q6uLA9BtunGApaI';
  const ARIA_LOGO_URL = 'https://cdn.shopify.com/s/files/1/0679/6574/7248/files/Untitled_design.png?v=1779914128';

  let sessionId = null;
  let conversationHistory = [];
  let isOpen = false;
  let isLoading = false;

  // ── Styles ─────────────────────────────────────────────────────────────────
  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500&family=IBM+Plex+Sans:wght@300;400;500;600&display=swap');

    :root {
      --aria-red: #960001;
      --aria-red-dark: #6e0001;
      --aria-red-glow: rgba(150, 0, 1, 0.22);
      --aria-dark: #0d0d0d;
      --aria-dark-2: #141414;
      --aria-dark-3: #1e1e1e;
      --aria-gray: #2a2a2a;
      --aria-gray-mid: #3a3a3a;
      --aria-steel: #7a7a7a;
      --aria-white: #ede9e4;
      --aria-z: 999999;
      --aria-bar-h: 56px;
      --aria-panel-w: 440px;
      --aria-mobile-bar-h: 64px;
    }

    /* ── Desktop Bar ── */
    #aria-bar {
      position: sticky;
      top: 0;
      left: 0;
      right: 0;
      width: 100%;
      height: var(--aria-bar-h);
      background: var(--aria-dark);
      border-bottom: 1px solid rgba(150,0,1,0.35);
      display: flex;
      align-items: center;
      gap: 14px;
      padding: 0 20px;
      z-index: var(--aria-z);
      font-family: 'IBM Plex Sans', sans-serif;
      box-shadow: 0 2px 20px rgba(0,0,0,0.6);
    }

    #aria-logo {
      height: 36px;
      width: auto;
      object-fit: contain;
      cursor: pointer;
      flex-shrink: 0;
      opacity: 0.95;
      transition: opacity 0.2s;
    }

    #aria-logo:hover { opacity: 1; }

    #aria-bar-divider {
      width: 1px;
      height: 28px;
      background: var(--aria-gray-mid);
      flex-shrink: 0;
    }

    #aria-bar-input-wrap {
      flex: 1;
      position: relative;
      display: flex;
      align-items: center;
    }

    #aria-bar-input {
      width: 100%;
      height: 36px;
      background: var(--aria-dark-3);
      border: 1px solid var(--aria-gray);
      border-radius: 2px;
      padding: 0 46px 0 14px;
      font-family: 'IBM Plex Sans', sans-serif;
      font-size: 13px;
      color: var(--aria-white);
      outline: none;
      transition: border-color 0.2s, box-shadow 0.2s;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    #aria-bar-input::placeholder {
      color: var(--aria-steel);
      font-size: 12px;
      letter-spacing: 0.01em;
    }

    #aria-bar-input:focus {
      border-color: var(--aria-red);
      box-shadow: 0 0 0 2px var(--aria-red-glow);
    }

    #aria-bar-send {
      position: absolute;
      right: 5px;
      width: 28px;
      height: 28px;
      background: var(--aria-red);
      border: none;
      border-radius: 2px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background 0.15s;
    }

    #aria-bar-send:hover { background: var(--aria-red-dark); }
    #aria-bar-send:disabled { background: var(--aria-gray); cursor: default; }
    #aria-bar-send svg { width: 13px; height: 13px; fill: white; }

    #aria-status {
      display: flex;
      align-items: center;
      gap: 5px;
      flex-shrink: 0;
    }

    #aria-dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: #2ecc71;
      box-shadow: 0 0 5px rgba(46,204,113,0.5);
    }

    #aria-dot.loading {
      background: var(--aria-red);
      animation: aria-pulse 1s ease-in-out infinite;
      box-shadow: 0 0 5px var(--aria-red-glow);
    }

    #aria-status-label {
      font-size: 10px;
      color: var(--aria-steel);
      letter-spacing: 0.06em;
      text-transform: uppercase;
      font-family: 'IBM Plex Mono', monospace;
    }

    /* ── Desktop Panel ── */
    #aria-panel {
      position: fixed;
      right: 0;
      bottom: 0;
      height: calc(100vh - var(--aria-bar-h));
      width: var(--aria-panel-w);
      max-width: 100vw;
      background: var(--aria-dark-2);
      border-left: 1px solid rgba(150,0,1,0.2);
      display: flex;
      flex-direction: column;
      z-index: calc(var(--aria-z) - 1);
      transform: translateX(100%);
      transition: transform 0.28s cubic-bezier(0.4,0,0.2,1);
      font-family: 'IBM Plex Sans', sans-serif;
    }

    #aria-panel.open { transform: translateX(0); }

    #aria-panel-header {
      padding: 12px 16px;
      border-bottom: 1px solid var(--aria-gray);
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-shrink: 0;
      background: var(--aria-dark);
    }

    #aria-panel-logo {
      height: 22px;
      width: auto;
      object-fit: contain;
    }

    #aria-panel-meta {
      font-size: 9px;
      color: var(--aria-steel);
      letter-spacing: 0.08em;
      text-transform: uppercase;
      font-family: 'IBM Plex Mono', monospace;
      margin-top: 3px;
    }

    #aria-close {
      width: 28px;
      height: 28px;
      background: transparent;
      border: 1px solid var(--aria-gray);
      border-radius: 2px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--aria-steel);
      transition: all 0.15s;
    }

    #aria-close:hover {
      border-color: var(--aria-red);
      color: var(--aria-white);
      background: rgba(150,0,1,0.1);
    }

    #aria-close svg { width: 13px; height: 13px; fill: currentColor; }

    #aria-messages {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
      padding: 16px;
      display: flex;
      flex-direction: column;
      gap: 12px;
      scrollbar-width: thin;
      scrollbar-color: var(--aria-gray) transparent;
      overscroll-behavior: contain;
    }

    #aria-messages::-webkit-scrollbar { width: 3px; }
    #aria-messages::-webkit-scrollbar-thumb { background: var(--aria-gray); border-radius: 2px; }

    #aria-quick-replies {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      padding: 10px 16px;
      border-top: 1px solid var(--aria-gray);
      flex-shrink: 0;
    }

    .aria-quick {
      font-size: 11px;
      padding: 5px 10px;
      background: var(--aria-dark-3);
      border: 1px solid var(--aria-gray);
      border-radius: 2px;
      color: var(--aria-steel);
      cursor: pointer;
      transition: all 0.15s;
      font-family: 'IBM Plex Sans', sans-serif;
    }

    .aria-quick:hover {
      border-color: var(--aria-red);
      color: var(--aria-white);
      background: rgba(150,0,1,0.08);
    }

    #aria-panel-input-wrap {
      padding: 12px 16px;
      border-top: 1px solid var(--aria-gray);
      display: flex;
      gap: 8px;
      flex-shrink: 0;
      background: var(--aria-dark);
    }

    #aria-panel-input {
      flex: 1;
      min-height: 38px;
      max-height: 100px;
      background: var(--aria-dark-3);
      border: 1px solid var(--aria-gray);
      border-radius: 2px;
      padding: 8px 12px;
      font-family: 'IBM Plex Sans', sans-serif;
      font-size: 16px;
      color: var(--aria-white);
      outline: none;
      resize: none;
      transition: border-color 0.2s, box-shadow 0.2s;
    }

    #aria-panel-input::placeholder { color: var(--aria-steel); font-size: 13px; }
    #aria-panel-input:focus { border-color: var(--aria-red); box-shadow: 0 0 0 2px var(--aria-red-glow); }

    #aria-panel-send {
      width: 38px;
      height: 38px;
      background: var(--aria-red);
      border: none;
      border-radius: 2px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      align-self: flex-end;
      transition: background 0.15s;
    }

    #aria-panel-send:hover { background: var(--aria-red-dark); }
    #aria-panel-send:disabled { background: var(--aria-gray); cursor: default; }
    #aria-panel-send svg { width: 14px; height: 14px; fill: white; }

    #aria-panel-footer {
      padding: 6px 16px;
      font-size: 9px;
      color: var(--aria-gray-mid);
      text-align: center;
      font-family: 'IBM Plex Mono', monospace;
      letter-spacing: 0.05em;
      background: var(--aria-dark);
      flex-shrink: 0;
    }

    /* ── Shared message styles ── */
    .aria-msg { display: flex; flex-direction: column; max-width: 90%; flex-shrink: 0; }
    .aria-msg.user { align-self: flex-end; align-items: flex-end; }
    .aria-msg.assistant { align-self: flex-start; align-items: flex-start; }

    .aria-msg-role {
      font-size: 9px;
      color: var(--aria-steel);
      letter-spacing: 0.07em;
      text-transform: uppercase;
      font-family: 'IBM Plex Mono', monospace;
      margin-bottom: 4px;
    }

    .aria-msg-bubble {
      padding: 9px 13px;
      font-size: 13px;
      line-height: 1.65;
      white-space: pre-wrap;
    }

    .aria-msg.user .aria-msg-bubble {
      background: var(--aria-red);
      color: #fff;
      border-radius: 2px 2px 0 2px;
    }

    .aria-msg.assistant .aria-msg-bubble {
      background: var(--aria-dark-3);
      color: var(--aria-white);
      border: 1px solid var(--aria-gray);
      border-radius: 2px 2px 2px 0;
    }

    .aria-msg.assistant .aria-msg-bubble strong { color: #fff; font-weight: 600; }

    .aria-msg.assistant .aria-msg-bubble a {
      color: #c84444;
      font-weight: 500;
      text-decoration: none;
    }

    .aria-typing {
      display: flex;
      gap: 4px;
      padding: 10px 13px;
      background: var(--aria-dark-3);
      border: 1px solid var(--aria-gray);
      border-radius: 2px 2px 2px 0;
      align-self: flex-start;
    }

    .aria-typing span {
      width: 5px;
      height: 5px;
      border-radius: 50%;
      background: var(--aria-red);
      animation: aria-bounce 1.2s ease-in-out infinite;
    }

    .aria-typing span:nth-child(2) { animation-delay: 0.2s; }
    .aria-typing span:nth-child(3) { animation-delay: 0.4s; }

    /* ── Desktop Overlay ── */
    #aria-overlay {
      position: fixed;
      inset: 0;
      background: rgba(0,0,0,0.4);
      z-index: calc(var(--aria-z) - 2);
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.28s;
    }

    #aria-overlay.visible { opacity: 1; pointer-events: all; }

    /* ── Part Card ── */
    .aria-part-card {
      background: var(--aria-dark-3);
      border: 1px solid var(--aria-gray);
      border-left: 2px solid var(--aria-red);
      border-radius: 2px 2px 2px 0;
      max-width: 90%;
      align-self: flex-start;
      overflow: hidden;
      flex-shrink: 0;
      font-family: 'IBM Plex Sans', sans-serif;
    }

    .aria-carousel {
      position: relative;
      width: 100%;
      overflow: hidden;
      background: #111;
    }

    .aria-carousel-inner {
      display: flex;
      transition: transform 0.3s ease;
    }

    .aria-carousel-inner img {
      width: 100%;
      flex-shrink: 0;
      display: block;
      object-fit: contain;
      height: 220px;
      cursor: zoom-in;
    }

    .aria-carousel-arrow {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      width: 32px;
      height: 32px;
      background: rgba(13,13,13,0.75);
      border: 1px solid rgba(150,0,1,0.4);
      border-radius: 2px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #ede9e4;
      font-size: 14px;
      z-index: 2;
      transition: background 0.15s;
      user-select: none;
    }

    .aria-carousel-arrow:hover { background: rgba(150,0,1,0.5); }
    .aria-carousel-arrow.left { left: 8px; }
    .aria-carousel-arrow.right { right: 8px; }

    .aria-card-body {
      padding: 12px;
    }

    .aria-card-commentary {
      font-size: 13px;
      color: var(--aria-white);
      line-height: 1.65;
      margin-bottom: 10px;
    }

    .aria-card-divider {
      height: 1px;
      background: var(--aria-gray);
      margin-bottom: 10px;
    }

    .aria-card-label {
      font-size: 9px;
      color: var(--aria-steel);
      letter-spacing: 0.07em;
      text-transform: uppercase;
      font-family: 'IBM Plex Mono', monospace;
      margin-bottom: 4px;
    }

    .aria-card-title {
      font-size: 13px;
      color: var(--aria-white);
      font-weight: 500;
      margin-bottom: 2px;
    }

    .aria-card-pn {
      font-size: 11px;
      color: var(--aria-steel);
      font-family: 'IBM Plex Mono', monospace;
      margin-bottom: 6px;
    }

    .aria-card-price {
      font-size: 16px;
      color: #fff;
      font-weight: 600;
      margin-bottom: 10px;
    }

    .aria-card-btn {
      width: 100%;
      padding: 10px;
      background: var(--aria-red);
      color: #fff;
      border: none;
      border-radius: 2px;
      cursor: pointer;
      font-family: 'IBM Plex Sans', sans-serif;
      font-size: 12px;
      font-weight: 600;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      transition: background 0.15s;
    }

    .aria-card-btn:hover { background: var(--aria-red-dark); }

    .aria-card-contact {
      display: block;
      width: 100%;
      padding: 10px;
      background: var(--aria-gray);
      color: var(--aria-white);
      text-align: center;
      border-radius: 2px;
      font-size: 12px;
      text-decoration: none;
      box-sizing: border-box;
    }

    /* ── Mobile Bar (bottom) ── */
    #aria-mobile-bar {
      display: none;
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      background: var(--aria-dark);
      border-top: 1px solid rgba(150,0,1,0.35);
      align-items: center;
      gap: 10px;
      padding: 12px 14px;
      padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px));
      z-index: var(--aria-z);
      box-shadow: 0 -2px 20px rgba(0,0,0,0.6);
    }

    #aria-mobile-logo {
      height: 36px;
      width: auto;
      object-fit: contain;
      flex-shrink: 0;
    }

    #aria-mobile-input {
      flex: 1;
      height: 38px;
      background: var(--aria-dark-3);
      border: 1px solid var(--aria-gray);
      border-radius: 2px;
      padding: 0 12px;
      font-family: 'IBM Plex Sans', sans-serif;
      font-size: 16px;
      color: var(--aria-white);
      outline: none;
      -webkit-appearance: none;
      min-width: 0;
    }

    #aria-mobile-input::placeholder { color: var(--aria-steel); font-size: 13px; }
    #aria-mobile-input:focus { border-color: var(--aria-red); }

    #aria-mobile-send {
      width: 38px;
      height: 38px;
      background: var(--aria-red);
      border: none;
      border-radius: 2px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    #aria-mobile-send svg { width: 13px; height: 13px; fill: white; }

    /* ── Mobile Full Screen Panel ── */
    #aria-mobile-panel {
      display: none;
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: var(--aria-dark-2);
      flex-direction: column;
      z-index: calc(var(--aria-z) + 1);
      height: 100%;
      overflow: hidden;
    }

    #aria-mobile-panel.open { display: flex; }

    #aria-mobile-panel-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 14px 16px;
      padding-top: calc(14px + env(safe-area-inset-top, 0px));
      background: var(--aria-dark);
      border-bottom: 1px solid var(--aria-gray);
      flex-shrink: 0;
    }

    #aria-mobile-panel-logo {
      height: 24px;
      width: auto;
      object-fit: contain;
    }

    #aria-mobile-panel-meta {
      font-size: 9px;
      color: var(--aria-steel);
      letter-spacing: 0.08em;
      text-transform: uppercase;
      font-family: 'IBM Plex Mono', monospace;
      margin-top: 2px;
    }

    #aria-mobile-close {
      width: 32px;
      height: 32px;
      background: transparent;
      border: 1px solid var(--aria-gray);
      border-radius: 2px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--aria-steel);
    }

    #aria-mobile-close svg { width: 14px; height: 14px; fill: currentColor; }

    #aria-mobile-messages {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
      padding: 16px;
      display: flex;
      flex-direction: column;
      gap: 12px;
      -webkit-overflow-scrolling: touch;
      overscroll-behavior: contain;
    }

    #aria-mobile-input-wrap {
      padding: 10px 14px;
      padding-bottom: calc(10px + env(safe-area-inset-bottom, 0px));
      border-top: 1px solid var(--aria-gray);
      display: flex;
      gap: 8px;
      flex-shrink: 0;
      background: var(--aria-dark);
    }

    #aria-mobile-panel-input {
      flex: 1;
      height: 38px;
      background: var(--aria-dark-3);
      border: 1px solid var(--aria-gray);
      border-radius: 2px;
      padding: 0 12px;
      font-family: 'IBM Plex Sans', sans-serif;
      font-size: 16px;
      color: var(--aria-white);
      outline: none;
      -webkit-appearance: none;
      min-width: 0;
    }

    #aria-mobile-panel-input::placeholder { color: var(--aria-steel); font-size: 13px; }
    #aria-mobile-panel-input:focus { border-color: var(--aria-red); }

    #aria-mobile-panel-send {
      width: 38px;
      height: 38px;
      background: var(--aria-red);
      border: none;
      border-radius: 2px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    #aria-mobile-panel-send svg { width: 14px; height: 14px; fill: white; }

    /* ── Responsive ── */
    @media (max-width: 749px) {
      #aria-bar { display: none !important; }
      #aria-panel { display: none !important; }
      #aria-overlay { display: none !important; }
      #aria-mobile-bar { display: flex; }
      body { padding-bottom: calc(var(--aria-mobile-bar-h) + env(safe-area-inset-bottom, 0px)) !important; background-color: var(--aria-dark) !important; }
    }

    @media (min-width: 750px) {
      #aria-mobile-bar { display: none !important; }
      #aria-mobile-panel { display: none !important; }
    }

    /* ── Animations ── */
    @keyframes aria-pulse {
      0%, 100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.4; transform: scale(0.75); }
    }

    @keyframes aria-bounce {
      0%, 80%, 100% { transform: translateY(0); opacity: 0.3; }
      40% { transform: translateY(-5px); opacity: 1; }
    }
  `;

  const styleEl = document.createElement('style');
  styleEl.textContent = styles;
  document.head.appendChild(styleEl);

  const sendIcon = `<svg viewBox="0 0 24 24"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>`;
  const closeIcon = `<svg viewBox="0 0 24 24"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>`;

  // ── Desktop bar ────────────────────────────────────────────────────────────
  const bar = document.createElement('div');
  bar.id = 'aria-bar';
  bar.innerHTML = `
    <img id="aria-logo" src="${ARIA_LOGO_URL}" alt="ARIA" />
    <div id="aria-bar-divider"></div>
    <div id="aria-bar-input-wrap">
      <input id="aria-bar-input" type="text" placeholder="Ask anything about your Multiquip equipment..." autocomplete="off" />
      <button id="aria-bar-send">${sendIcon}</button>
    </div>
    <div id="aria-status">
      <div id="aria-dot"></div>
      <span id="aria-status-label">Online</span>
    </div>
  `;
  const mount = document.getElementById('aria-bar-mount');
  if (mount) {
    mount.appendChild(bar);
  } else {
    document.body.prepend(bar);
  }

  // ── Desktop overlay ────────────────────────────────────────────────────────
  const overlay = document.createElement('div');
  overlay.id = 'aria-overlay';
  document.body.appendChild(overlay);

  // ── Desktop panel ──────────────────────────────────────────────────────────
  const panel = document.createElement('div');
  panel.id = 'aria-panel';
  panel.innerHTML = `
    <div id="aria-panel-header">
      <div>
        <img id="aria-panel-logo" src="${ARIA_LOGO_URL}" alt="ARIA" />
        <div id="aria-panel-meta">CCE Industries · Technical Support</div>
      </div>
      <button id="aria-close">${closeIcon}</button>
    </div>
    <div id="aria-messages"></div>
    <div id="aria-quick-replies">
      <button class="aria-quick">Part for my rammer</button>
      <button class="aria-quick">Plate compactor issue</button>
      <button class="aria-quick">Generator specs</button>
      <button class="aria-quick">Oil type for my equipment</button>
    </div>
    <div id="aria-panel-input-wrap">
      <textarea id="aria-panel-input" rows="1" placeholder="Ask anything about your equipment..."></textarea>
      <button id="aria-panel-send">${sendIcon}</button>
    </div>
    <div id="aria-panel-footer">ARIA · CCE Industries</div>
  `;
  document.body.appendChild(panel);

  // ── Mobile bar ─────────────────────────────────────────────────────────────
  const mobileBar = document.createElement('div');
  mobileBar.id = 'aria-mobile-bar';
  mobileBar.innerHTML = `
    <img id="aria-mobile-logo" src="${ARIA_LOGO_URL}" alt="ARIA" />
    <input id="aria-mobile-input" type="text" placeholder="Ask ARIA..." autocomplete="off" />
    <button id="aria-mobile-send">${sendIcon}</button>
  `;
  document.body.appendChild(mobileBar);

  // ── Mobile full screen panel ───────────────────────────────────────────────
  const mobilePanel = document.createElement('div');
  mobilePanel.id = 'aria-mobile-panel';
  mobilePanel.innerHTML = `
    <div id="aria-mobile-panel-header">
      <div>
        <img id="aria-mobile-panel-logo" src="${ARIA_LOGO_URL}" alt="ARIA" />
        <div id="aria-mobile-panel-meta" style="font-size:9px;color:#7a7a7a;letter-spacing:0.08em;text-transform:uppercase;font-family:'IBM Plex Mono',monospace;margin-top:2px;">CCE Industries · Technical Support</div>
      </div>
      <button id="aria-mobile-close">${closeIcon}</button>
    </div>
    <div id="aria-mobile-messages"></div>
    <div id="aria-mobile-input-wrap">
      <input id="aria-mobile-panel-input" type="text" placeholder="Ask anything..." autocomplete="off" />
      <button id="aria-mobile-panel-send">${sendIcon}</button>
    </div>
  `;
  document.body.appendChild(mobilePanel);

  // ── Helpers ────────────────────────────────────────────────────────────────
  function isMobile() { return window.innerWidth < 750; }

  function activeMessagesEl() {
    return isMobile()
      ? document.getElementById('aria-mobile-messages')
      : document.getElementById('aria-messages');
  }

  function stripMarkdown(text) {
    return text
      .replace(/\*\*Part Number:\*\*.*$/gm, '')
      .replace(/\*\*Description:\*\*.*$/gm, '')
      .replace(/\*\*Price:\*\*.*$/gm, '')
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/!\[.*?\]\(.*?\)/g, '')
      .replace(/\[([^\]]+)\]\(.*?\)/g, '$1')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }

  function formatMessage(text) {
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/!\[([^\]]*)\]\((https?:\/\/[^\)]+)\)/g,
        '<img src="$2" alt="$1" onclick="window.open(this.src,\'_blank\')" style="max-width:100%;border-radius:2px;margin-top:10px;display:block;cursor:zoom-in;border:1px solid #2a2a2a;" />')
      .replace(/\[([^\]]+)\]\((https?:\/\/[^\)]+)\)/g,
        '<a href="$2" target="_blank" rel="noreferrer">$1</a>');
  }

  function addMessage(role, text) {
    const el = activeMessagesEl();
    if (!el) return;

    if (el.children.length > 0) {
      const qr = document.getElementById('aria-quick-replies');
      if (qr) qr.style.display = 'none';
    }

    const msg = document.createElement('div');
    msg.className = `aria-msg ${role}`;
    msg.innerHTML = `
      <div class="aria-msg-role">${role === 'user' ? 'You' : 'ARIA'}</div>
      <div class="aria-msg-bubble">${formatMessage(text)}</div>
    `;
    el.appendChild(msg);
    el.scrollTop = el.scrollHeight;
  }

  function buildCarousel(images) {
    if (images.length === 0) return '';

    const carousel = document.createElement('div');
    carousel.className = 'aria-carousel';

    const inner = document.createElement('div');
    inner.className = 'aria-carousel-inner';

    images.forEach(src => {
      const img = document.createElement('img');
      img.src = src;
      img.onclick = () => window.open(src, '_blank');
      inner.appendChild(img);
    });

    carousel.appendChild(inner);

    if (images.length > 1) {
      let current = 0;

      const leftArrow = document.createElement('div');
      leftArrow.className = 'aria-carousel-arrow left';
      leftArrow.innerHTML = '&#8592;';
      leftArrow.style.display = 'none';

      const rightArrow = document.createElement('div');
      rightArrow.className = 'aria-carousel-arrow right';
      rightArrow.innerHTML = '&#8594;';

      const update = () => {
        inner.style.transform = `translateX(-${current * 100}%)`;
        leftArrow.style.display = current === 0 ? 'none' : 'flex';
        rightArrow.style.display = current === images.length - 1 ? 'none' : 'flex';
      };

      leftArrow.addEventListener('click', () => { if (current > 0) { current--; update(); } });
      rightArrow.addEventListener('click', () => { if (current < images.length - 1) { current++; update(); } });

      carousel.appendChild(leftArrow);
      carousel.appendChild(rightArrow);
    }

    return carousel;
  }

  function renderPartCard(commentary, part) {
    const el = activeMessagesEl();
    if (!el) return;

    if (el.children.length > 0) {
      const qr = document.getElementById('aria-quick-replies');
      if (qr) qr.style.display = 'none';
    }

    const card = document.createElement('div');
    card.className = 'aria-part-card';

    // Build image array — diagram first, then product image
    const images = [];
    if (part.diagram_src) images.push(part.diagram_src);
    if (part.image_src) images.push(part.image_src);

    // Carousel
    if (images.length > 0) {
      const carousel = buildCarousel(images);
      card.appendChild(carousel);
    }

    // Body
    const body = document.createElement('div');
    body.className = 'aria-card-body';

    // Commentary
    const cleanText = stripMarkdown(commentary);
    if (cleanText) {
      const comm = document.createElement('div');
      comm.className = 'aria-card-commentary';
      comm.textContent = cleanText;
      body.appendChild(comm);

      const divider = document.createElement('div');
      divider.className = 'aria-card-divider';
      body.appendChild(divider);
    }

    // Part label
    const label = document.createElement('div');
    label.className = 'aria-card-label';
    label.textContent = 'Part';
    body.appendChild(label);

    // Title
    const title = document.createElement('div');
    title.className = 'aria-card-title';
    title.textContent = part.title || '';
    body.appendChild(title);

    // Part number
    const pn = document.createElement('div');
    pn.className = 'aria-card-pn';
    pn.textContent = part.part_number;
    body.appendChild(pn);

    // Price
    const price = document.createElement('div');
    price.className = 'aria-card-price';
    price.textContent = `$${parseFloat(part.price).toFixed(2)}`;
    body.appendChild(price);

    // Button
    if (part.variant_id) {
      const btn = document.createElement('button');
      btn.className = 'aria-card-btn';
      btn.textContent = 'Add to Cart';
      btn.addEventListener('click', async () => {
        try {
          const resp = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id: part.variant_id, quantity: 1 }] })
          });
          const data = await resp.json();
          if (data.items) {
            btn.textContent = 'Added ✓';
            btn.style.background = '#2ecc71';
            setTimeout(() => {
              btn.textContent = 'Add to Cart';
              btn.style.background = '';
            }, 2000);
          }
        } catch {
          btn.textContent = 'Error — Try Again';
        }
      });
      body.appendChild(btn);
    } else {
      const contact = document.createElement('a');
      contact.className = 'aria-card-contact';
      contact.href = 'mailto:admin@cce-industries.com';
      contact.textContent = 'Contact for Availability';
      body.appendChild(contact);
    }

    card.appendChild(body);
    el.appendChild(card);
    el.scrollTop = el.scrollHeight;
  }

  function showTyping() {
    const el = activeMessagesEl();
    if (!el) return;
    const t = document.createElement('div');
    t.className = 'aria-typing';
    t.id = 'aria-typing-indicator';
    t.innerHTML = '<span></span><span></span><span></span>';
    el.appendChild(t);
    el.scrollTop = el.scrollHeight;
  }

  function hideTyping() {
    const t = document.getElementById('aria-typing-indicator');
    if (t) t.remove();
  }

  function setLoading(val) {
    isLoading = val;
    const dot = document.getElementById('aria-dot');
    if (dot) dot.className = val ? 'loading' : '';
    ['aria-bar-send', 'aria-panel-send', 'aria-mobile-send', 'aria-mobile-panel-send'].forEach(id => {
      const btn = document.getElementById(id);
      if (btn) btn.disabled = val;
    });
  }

  function openPanel() {
    if (isOpen) return;
    isOpen = true;
    if (isMobile()) {
      mobilePanel.classList.add('open');
      setTimeout(() => {
        const input = document.getElementById('aria-mobile-panel-input');
        if (input) input.focus();
      }, 100);
    } else {
      panel.classList.add('open');
      overlay.classList.add('visible');
      setTimeout(() => {
        const input = document.getElementById('aria-panel-input');
        if (input) input.focus();
      }, 100);
    }
    if (activeMessagesEl().children.length === 0) {
      addMessage('assistant', "I'm ARIA, CCE Industries' AI-powered technical support specialist for Multiquip and Wacker Neuson equipment.\n\nWhat equipment are you working on?");
    }
  }

  function closePanel() {
    isOpen = false;
    panel.classList.remove('open');
    overlay.classList.remove('visible');
    mobilePanel.classList.remove('open');
    document.activeElement && document.activeElement.blur();
  }

  // ── Send ───────────────────────────────────────────────────────────────────
  async function send(text, sourceInput) {
    if (!text.trim() || isLoading) return;

    openPanel();
    addMessage('user', text);
    conversationHistory.push({ role: 'user', content: text });

    ['aria-bar-input', 'aria-panel-input', 'aria-mobile-input', 'aria-mobile-panel-input'].forEach(id => {
      const el = document.getElementById(id);
      if (el) el.value = '';
    });

    if (sourceInput) sourceInput.blur();
    document.activeElement && document.activeElement.blur();

    setLoading(true);
    showTyping();

    try {
      const resp = await fetch(EDGE_FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          message: text,
          conversation_history: conversationHistory.slice(-10),
          session_id: sessionId
        })
      });

      const data = await resp.json();
      hideTyping();

      if (data.reply) {
        sessionId = data.session_id;
        conversationHistory.push({ role: 'assistant', content: data.reply });

        if (data.parts && data.parts.length > 0) {
          // Render unified part card(s) — commentary + part info together
          data.parts.forEach(part => renderPartCard(data.reply, part));
        } else {
          // No parts — plain message
          addMessage('assistant', data.reply);
        }
      } else {
        addMessage('assistant', 'Something went wrong. Email us at admin@cce-industries.com.');
      }
    } catch {
      hideTyping();
      addMessage('assistant', 'Connection error. Email us at admin@cce-industries.com.');
    }

    setLoading(false);
  }

  // ── Event listeners ────────────────────────────────────────────────────────

  // Desktop bar
  const barInput = document.getElementById('aria-bar-input');
  barInput.addEventListener('keydown', e => {
    if (e.key === 'Enter') send(barInput.value, barInput);
  });
  document.getElementById('aria-bar-send').addEventListener('click', () => send(barInput.value, barInput));
  document.getElementById('aria-logo').addEventListener('click', () => isOpen ? closePanel() : openPanel());

  // Desktop panel
  document.getElementById('aria-close').addEventListener('click', closePanel);
  overlay.addEventListener('click', closePanel);
  const panelInput = document.getElementById('aria-panel-input');
  panelInput.addEventListener('keydown', e => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(panelInput.value, panelInput); }
  });
  document.getElementById('aria-panel-send').addEventListener('click', () => send(panelInput.value, panelInput));

  // Quick replies
  document.querySelectorAll('.aria-quick').forEach(btn => {
    btn.addEventListener('click', () => send(btn.textContent, btn));
  });

  // Mobile bar
  const mobileInput = document.getElementById('aria-mobile-input');
  mobileInput.addEventListener('keydown', e => {
    if (e.key === 'Enter') send(mobileInput.value, mobileInput);
  });
  document.getElementById('aria-mobile-send').addEventListener('click', () => send(mobileInput.value, mobileInput));

  // Mobile panel
  document.getElementById('aria-mobile-close').addEventListener('click', closePanel);
  const mobilePanelInput = document.getElementById('aria-mobile-panel-input');
  mobilePanelInput.addEventListener('keydown', e => {
    if (e.key === 'Enter') send(mobilePanelInput.value, mobilePanelInput);
  });
  document.getElementById('aria-mobile-panel-send').addEventListener('click', () => send(mobilePanelInput.value, mobilePanelInput));

  // ── Mobile keyboard focus fix ──────────────────────────────────────────────
  mobileInput.addEventListener('focus', () => {
    document.body.style.backgroundColor = '#0d0d0d';
  });
  mobileInput.addEventListener('blur', () => {
    document.body.style.backgroundColor = '';
  });

})();